import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Button,
  Text,
  Flex,
  Box,
  Center,
  VStack,
  Link,
  CircularProgress,
  useToast,
  CloseButton,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { ReclaimProofRequest } from '@reclaimprotocol/js-sdk';
import QRCode from 'react-qr-code';
import { useAppSelector } from '../../redux/hooks';
import { getParamsAdded } from '../../redux/providerBuilder/selectedInstances/selectors';
import { getManualVerifcationProviderVerificationUrl } from '../../utils/manual-verification-session/utils';

interface DemoProviderModalProps {
  isOpen: boolean;
  onClose: () => void;
  httpProviderId: string;
}

export const DemoProviderModal = ({
  isOpen,
  onClose,
  httpProviderId,
}: DemoProviderModalProps) => {
  const toast = useToast();
  const [url, setUrl] = useState('');
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const paramsAdded = useAppSelector(getParamsAdded);

  useEffect(() => {
    if (!url && httpProviderId) {
      generateVerificationRequest();
    }
  }, [url, httpProviderId]);

  async function generateVerificationRequest() {
    try {
      setButtonLoading(true);
      const response = await getManualVerifcationProviderVerificationUrl(
        httpProviderId,
        paramsAdded
      );
      if (response?.message === 'successful') {
        const reclaimProofRequest = await ReclaimProofRequest.fromJsonString(
          response?.reclaimProofRequestConfig
        );
        const requestUrl = await reclaimProofRequest.getRequestUrl();
        setUrl(requestUrl);

        await reclaimProofRequest.startSession({
          onSuccess: (proofs) => {
            if (proofs) {
              console.log('Proof received:', proofs);
              toast({
                title: 'Provider Test Successful',
                description: 'Proof submitted successfully',
                position: 'top-right',
                status: 'success',
                duration: 5000,
                isClosable: true,
              });
              setUrl('');
              onClose();
            }
          },
          onError: (error) => {
            console.error('Verification failed', error);
            toast({
              title: 'Provider Test Failed',
              description: 'Please contact support or try again',
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
            setUrl('');
            onClose();
          },
        });
      } else {
        throw Error('Could not generate verification link');
      }
    } catch (error) {
      toast({
        title: 'Provider verification failed',
        description: 'Could not generate verification link',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      console.error('Verification failed', error);
    } finally {
      setButtonLoading(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setUrl('');
        onClose();
      }}
      isCentered
      size="md"
    >
      <ModalOverlay />
      <ModalContent maxW="450px">
        <ModalHeader
          textAlign="center"
          borderBottom="1px solid #E2E8F0"
          pb={4}
          bg="#F7FAFC"
          borderTopRadius="md"
        >
          <Flex justifyContent="space-between" alignItems="center">
            <Text fontWeight="600" fontSize="lg">
              Try Now
            </Text>
            <CloseButton
              onClick={() => {
                setUrl('');
                onClose();
              }}
              size="md"
              _hover={{
                bg: 'gray.200',
              }}
            />
          </Flex>
        </ModalHeader>

        <ModalBody py={8} px={6}>
          <VStack spacing={4} align="stretch">
            <Center>
              {url ? (
                <VStack spacing={2} align="center">
                  <Box
                    p={4}
                    bg="white"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                  >
                    <QRCode size={200} value={url} style={{ borderRadius: '8px' }} />

                    <Flex
                      align="center"
                      justify="center"
                      gap={3}
                      mt={4}
                      p={3}
                      bg="blue.50"
                      borderColor="blue.100"
                    >
                      <CircularProgress
                        size="20px"
                        color="blue.500"
                        isIndeterminate
                        thickness="3px"
                        trackColor="blue.100"
                      />
                      <Text
                        color="blue.700"
                        textAlign="center"
                        fontSize="sm"
                        fontWeight="600"
                        letterSpacing="0.2px"
                      >
                        Waiting for Proof
                      </Text>
                    </Flex>

                    <Text color="gray.600" fontSize="sm" mt={3} textAlign="center">
                      Scan this QR code
                    </Text>
                  </Box>

                  <VStack spacing={2}>
                    <Text fontSize="sm" color="gray.600" fontWeight="500">
                      Or visit
                    </Text>
                    <Link
                      href={url}
                      isExternal
                      color="blue.600"
                      fontSize="sm"
                      maxW="300px"
                      fontWeight="500"
                      _hover={{
                        color: 'blue.700',
                        textDecoration: 'underline',
                      }}
                      display="flex"
                      alignItems="center"
                      gap={1}
                    >
                      {url?.slice(0, 35) + '...' + url?.slice(-5)}
                    </Link>
                  </VStack>
                </VStack>
              ) : (
                <Flex
                  width="200px"
                  height="200px"
                  alignItems="center"
                  justifyContent="center"
                >
                  <CircularProgress
                    color="blue.500"
                    isIndeterminate
                    size="50px"
                    thickness="4px"
                    trackColor="gray.100"
                  />
                </Flex>
              )}
            </Center>

            <Button
              colorScheme="blue"
              width="full"
              size="lg"
              onClick={generateVerificationRequest}
              isLoading={buttonLoading}
              loadingText="Generating link..."
              transition="all 0.2s"
              fontWeight="600"
              _hover={{
                transform: 'translateY(-1px)',
                boxShadow: 'lg',
              }}
              _active={{
                transform: 'translateY(0)',
                boxShadow: 'md',
              }}
            >
              Generate New Verification Link
            </Button>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
